import React from 'react';
import { Search, MapPin, Briefcase, Check } from 'lucide-react';
import { Input } from '../ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';

/**
 * Searchable job picker — replaces a plain <Select> with a popover combobox
 * that supports typeahead across title, client and location.
 *
 * Designed for the "Add to Pipeline" / workbench shortlist dialogs where
 * recruiters may have 200+ open jobs and need to find one by partial match.
 *
 * Props:
 *  - jobs: [{ id, title, client_name, location, status }]
 *  - value: selected job id (string) or ''
 *  - onChange: (jobId: string) => void
 *  - placeholder: trigger placeholder when no job selected
 *  - testid: prefix for data-testid on trigger + each option
 *  - disabled: bool — disables the trigger
 *  - allowNone: bool — when true, prepends a "None — workbench only" option
 *  - noneLabel: custom label for the none option (default "None — workbench only")
 */
const JobPicker = ({
  jobs = [],
  value = '',
  onChange,
  placeholder = 'Pick a job…',
  testid = 'job-picker',
  disabled = false,
  allowNone = false,
  noneLabel = 'None — workbench only',
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const inputRef = React.useRef(null);

  const selected = React.useMemo(
    () => (value ? jobs.find((j) => j.id === value) : null),
    [jobs, value]
  );

  // Case-insensitive substring match across title, client_name, location.
  // Whitespace-separated tokens are AND-ed so users can type
  // "head mumbai" to find a State Head role located in Mumbai.
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return jobs;
    const tokens = q.split(/\s+/).filter(Boolean);
    return jobs.filter((j) => {
      const hay = `${j.title || ''} ${j.client_name || ''} ${j.location || ''}`.toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [jobs, query]);

  // Reset query whenever the popover closes
  React.useEffect(() => {
    if (!open) setQuery('');
    else {
      // small delay so Popover focus trap doesn't steal focus back
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const handlePick = (jobId) => {
    onChange?.(jobId);
    setOpen(false);
  };

  const noJobs = jobs.length === 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || noJobs}
          data-testid={`${testid}-trigger`}
          className="w-full justify-between font-normal h-10 px-3"
        >
          <span className="flex items-center gap-2 min-w-0 truncate">
            <Briefcase className="w-4 h-4 text-slate-400 flex-shrink-0" />
            {selected ? (
              <span className="flex items-center gap-1.5 min-w-0 truncate text-slate-900">
                <span className="font-medium truncate">{selected.title}</span>
                {selected.client_name ? (
                  <span className="text-slate-500 truncate">· {selected.client_name}</span>
                ) : null}
                {selected.location ? (
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-600 bg-slate-100 border border-slate-200 rounded px-1 py-0 flex-shrink-0">
                    <MapPin className="w-2.5 h-2.5" />
                    {selected.location}
                  </span>
                ) : null}
              </span>
            ) : (
              <span className="text-slate-500 truncate">
                {noJobs ? 'No active jobs available' : placeholder}
              </span>
            )}
          </span>
          <svg className="ml-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[320px] max-h-[60vh] overflow-hidden flex flex-col"
        align="start"
        sideOffset={4}
        collisionPadding={12}
        data-testid={`${testid}-popover`}
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2 flex-shrink-0">
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, client, location…"
            className="h-7 border-0 px-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm shadow-none"
            data-testid={`${testid}-search`}
          />
          {query ? (
            <span className="text-[10px] text-slate-400 flex-shrink-0">
              {filtered.length}/{jobs.length}
            </span>
          ) : null}
        </div>
        {/* Plain overflow-y-auto wrapper instead of shadcn ScrollArea —
            ScrollArea requires a fixed height to scroll, but we want the
            list to grow/shrink with viewport AND scroll when overflowing.
            Using a flex-1 + overflow-y-auto pair is more reliable inside
            a Popover that opens above/below an in-dialog trigger. */}
        <div className="flex-1 overflow-y-auto" data-testid={`${testid}-scroll`}>
          <ul className="py-1" data-testid={`${testid}-list`}>
            {allowNone ? (
              <li>
                <button
                  type="button"
                  onClick={() => handlePick('')}
                  data-testid={`${testid}-option-none`}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-2 ${
                    value === '' ? 'bg-teal-50/60' : ''
                  }`}
                >
                  <span className="italic text-slate-500">{noneLabel}</span>
                  {value === '' ? <Check className="w-4 h-4 text-teal-600 flex-shrink-0" /> : null}
                </button>
              </li>
            ) : null}
            {filtered.length === 0 ? (
              <li className="px-3 py-6 text-center text-xs text-slate-400" data-testid={`${testid}-empty`}>
                No jobs match "{query}"
              </li>
            ) : (
              filtered.map((j) => (
                <li key={j.id}>
                  <button
                    type="button"
                    onClick={() => handlePick(j.id)}
                    data-testid={`${testid}-option-${j.id}`}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-2 ${
                      value === j.id ? 'bg-teal-50/60' : ''
                    }`}
                  >
                    <span className="flex items-center gap-1.5 min-w-0 truncate">
                      <span className="font-medium text-slate-900 truncate">{j.title}</span>
                      {j.client_name ? (
                        <span className="text-slate-500 truncate"> · {j.client_name}</span>
                      ) : null}
                      {j.location ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-600 bg-slate-100 border border-slate-200 rounded px-1 py-0 ml-1 flex-shrink-0">
                          <MapPin className="w-2.5 h-2.5" />
                          {j.location}
                        </span>
                      ) : null}
                    </span>
                    {value === j.id ? <Check className="w-4 h-4 text-teal-600 flex-shrink-0" /> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default JobPicker;
